import {
  Autocomplete,
  Avatar,
  Box,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  BackButton,
  getInitials,
  IEstablishment,
  LanguagePicker,
  PreferenceRow,
  stringToColor,
  ThemeSwitch,
  useConfig,
} from "@4uhub/lib4uhub";

import useNotificationPreference from "../../hooks/useNotificationPreference";

const translationPath = "page.user_menu.";

const PreferencesPage = () => {
  const { t } = useTranslation();

  const {
    configuration: { establishment },
    establishments,
    setConfig,
  } = useConfig();

  const changeEstablishmentHandler = useCallback(
    (newEstablishment: IEstablishment | null) => {
      if (!newEstablishment || newEstablishment.id === establishment.id) return;

      setConfig({ establishment: newEstablishment });
    },
    [establishment, setConfig]
  );

  const {
    enabled: notificationsEnabled,
    permission: notificationPermission,
    changeHandler: notificationsChangeHandler,
  } = useNotificationPreference();

  return (
    <Stack sx={{ height: "100%", overflow: "auto" }}>
      <Stack mb={2} alignItems={"flex-start"}>
        <BackButton />
        <Typography variant="h5" mt={2}>
          {t(translationPath + "preferences")}
        </Typography>
      </Stack>

      <Stack direction="column" spacing="8px" maxWidth={600}>
        <PreferenceRow
          label={String(t(translationPath + "establishment"))}
          description={String(t(translationPath + "establishment_description"))}
        >
          <Autocomplete<IEstablishment>
            size="small"
            sx={{ minWidth: 240 }}
            options={establishments}
            value={establishment.id ? establishment : null}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <Box component="li" {...{ ...props, key: option.id }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={(theme) => ({
                      bgcolor: stringToColor(option.id),
                      width: 30,
                      height: 30,
                      fontSize: "0.75rem",
                      color: theme.palette.getContrastText(
                        stringToColor(option.id)
                      ),
                    })}
                  >
                    {getInitials(option.name)}
                  </Avatar>
                  <Typography variant="body2">{option.name}</Typography>
                </Stack>
              </Box>
            )}
            onChange={(_, newValue) => changeEstablishmentHandler(newValue)}
            renderInput={(params) => <TextField {...params} />}
          />
        </PreferenceRow>

        <PreferenceRow
          label={String(t(translationPath + "theme"))}
          description={String(t(translationPath + "theme_description"))}
        >
          <ThemeSwitch
            iconButtonProps={{
              size: "medium",
              color: "inherit",
            }}
          />
        </PreferenceRow>

        <PreferenceRow
          label={String(t(translationPath + "language"))}
          description={String(t(translationPath + "language_description"))}
        >
          <LanguagePicker />
        </PreferenceRow>

        <PreferenceRow
          label={String(t(translationPath + "notifications"))}
          description={String(
            notificationsEnabled && notificationPermission === "denied"
              ? t(translationPath + "notifications_blocked")
              : t(translationPath + "notifications_description")
          )}
        >
          <Switch
            checked={notificationsEnabled}
            onChange={(_, checked) => notificationsChangeHandler(checked)}
            inputProps={{
              "aria-label": String(t(translationPath + "notifications")),
            }}
          />
        </PreferenceRow>
      </Stack>
    </Stack>
  );
};

export default PreferencesPage;
